// src/pages/HajjGuidePage/HajjGuidePage.tsx
import React from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Navigation from '../../components/Navigation/Navigation';
import guideHajjData from '../../data/guide-hajj.json';
import './HajjGuidePage.css';
import SEO from '../../components/SEO/SEO';

interface InvocationItem {
  arabic: string;
  phonetic: string;
  translation: string;
}

interface InvocationMethodItem {
  title: string;
  description?: string;
  invocation?: InvocationItem;
  instruction?: string;
}

interface EnumerationItem {
  label: string;
  text: string;
  subtext?: string;
}

interface HadithItem {
  text: string;
  source?: string;
  context?: string;
  reference?: string;
}

interface ContentItem {
  type: string;
  text?: string;
  number?: number;
  label?: string;
  arabic?: string;
  phonetic?: string;
  translation?: string;
  reference?: string;
  source?: string;
  context?: string;
  items?: string[] | EnumerationItem[];
  methods?: InvocationMethodItem[];
  hadiths?: HadithItem[];
  conclusion?: string;
  mainMessage?: string;
  title?: string;
  content?: string;
  important?: boolean;
  id?: string;
  link?: { href: string; label: string };
  sheikh?: string;
  caption?: string;
  qa?: Array<{ question: string; answer: string; points?: string[] }>;
  sections?: Array<{ label: string; text: string }>;
  headers?: string[];
  rows?: string[][];
  footer?: Array<{ label: string; value: string }>;
}

interface GuideData {
  title: string;
  subtitle: string;
  author: string;
  description: string;
  conference_info: {
    title_ar: string;
    location: string;
    date: string;
  };
  content: ContentItem[];
}

const HajjGuidePage: React.FC = () => {
  const data = guideHajjData as GuideData;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  let currentPart = 0;

  const renderContentItem = (item: ContentItem, index: number) => {
    if (item.type === 'part-title') {
      currentPart = item.number || 0;
    }

    switch (item.type) {
      case 'introduction':
        return (
          <div key={index} className="hajj-introduction">
            <p>{item.text}</p>
          </div>
        );

      case 'part-title':
        return (
          <div key={index} className={`hajj-part-title part-${item.number}`}>
            <div className="hajj-part-number">{item.number}</div>
            <h2 className="hajj-part-text">{item.text}</h2>
          </div>
        );

      case 'section-title':
        return (
          <h3 key={index} className="hajj-section-title">
            <span className="hajj-section-number">{item.number}.</span>
            {item.text}
          </h3>
        );

      case 'subsection-title': {
        const isMainSub = /^\d+[-–]|^✦/.test(item.text || '');
        const mainClass = isMainSub ? ' hajj-subsection-main' : '';
        return (
          <div key={index} className="hajj-subsection-title-wrapper">
            {item.id && (
              <button className="hajj-back-btn" onClick={() => window.history.back()} title="Revenir à l'endroit précédent">
                ↑ Retour
              </button>
            )}
            <h4 id={item.id} className={`hajj-subsection-title${mainClass} part-${currentPart}`}>{item.text}</h4>
          </div>
        );
      }

      case 'paragraph': {
        const renderTextWithQuotes = (text: string): React.ReactNode => {
          if (!text.includes('«')) return text;
          const parts = text.split(/(«[^»]*»)/g);
          return <>{parts.map((part, i) =>
            part.startsWith('«') ? <strong key={i}><em>{part}</em></strong> : part
          )}</>;
        };

        const renderParagraphContent = (text: string) => {
          if (text.includes('Pour l\'homme :') || text.includes('Pour les hommes :') ||
              text.includes('Pour la femme :') || text.includes('Pour les femmes :')) {
            const parts = text.split(/(:)/);
            const rest = parts.slice(2).join('');
            return (
              <p>
                <strong className="hajj-paragraph-gender">{parts[0]}{parts[1]}</strong>
                {renderTextWithQuotes(rest)}
              </p>
            );
          }

          if (text.trim().endsWith(':') || text.includes('Il est recommandé')) {
            return <p className="hajj-paragraph-intro">{renderTextWithQuotes(text)}</p>;
          }

          return <p>{renderTextWithQuotes(text)}</p>;
        };

        return (
          <div key={index} className={`hajj-paragraph${item.important ? ' hajj-paragraph-important' : ''}`}>
            {item.text?.split('\n').map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {renderParagraphContent(line)}
              </React.Fragment>
            ))}
          </div>
        );
      }

      case 'list':
        return (
          <ul key={index} className="hajj-list">
            {(item.items as string[])?.map((listItem, listIndex) => (
              <li key={listIndex}>{listItem}</li>
            ))}
          </ul>
        );

      case 'highlight':
        return (
          <div key={index} className="hajj-highlight">
            {item.label && <div className="hajj-highlight-label">{item.label}</div>}
            <div className="hajj-highlight-text">
              {item.text?.split('\n').map((line, lineIndex) => (
                <p key={lineIndex}>{line}</p>
              ))}
            </div>
            {item.link && (
              <a
                href={item.link.href}
                className="hajj-highlight-link"
                {...(item.link.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {item.link.label}
              </a>
            )}
          </div>
        );

      case 'important-note':
        return (
          <div key={index} className="hajj-important-note">
            <div className="hajj-note-icon">⚠️</div>
            <div className="hajj-note-content">
              <div className="hajj-note-text">{item.text}</div>
              {item.link && (
                <a href={item.link.href} className="hajj-note-link">
                  {item.link.label}
                </a>
              )}
            </div>
          </div>
        );

      case 'invocation':
        return (
          <div key={index} className={`hajj-invocation-card part-${currentPart}`}>
            <div className="hajj-invocation-header">
              <div className="hajj-invocation-icon">🤲</div>
              <div className="hajj-invocation-title">Invocation</div>
            </div>
            <div className="hajj-invocation-body">
              <div className="hajj-invocation-arabic">{item.arabic}</div>
              <div className="hajj-invocation-phonetic">{item.phonetic}</div>
              <div className="hajj-invocation-translation">{item.translation}</div>
            </div>
          </div>
        );

      case 'invocation-choice':
        return (
          <div key={index} className={`hajj-invocation-card part-${currentPart}`}>
            <div className="hajj-invocation-header">
              <div className="hajj-invocation-icon">🤲</div>
              <div className="hajj-invocation-title">{item.label || 'Invocation'}</div>
            </div>
            <div className="hajj-invocation-body">
              <div className="hajj-invocation-arabic">{item.arabic}</div>
              <div className="hajj-invocation-phonetic">{item.phonetic}</div>
              <div className="hajj-invocation-translation">{item.translation}</div>
            </div>
          </div>
        );

      case 'verse':
        return (
          <div key={index} className={`hajj-verse-card part-${currentPart}`}>
            <div className="hajj-verse-header">
              <div className="hajj-verse-icon">📖</div>
              <div className="hajj-verse-title">Verset coranique</div>
            </div>
            <div className="hajj-verse-body">
              <div className="hajj-verse-arabic">{item.arabic}</div>
              <div className="hajj-verse-phonetic">{item.phonetic}</div>
              <div className="hajj-verse-translation">{item.translation}</div>
              {item.reference && (
                <div className="hajj-verse-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'hadith':
        return (
          <div key={index} className="hajj-hadith-card">
            <div className="hajj-hadith-header">
              <div className="hajj-hadith-icon">📜</div>
              <div className="hajj-hadith-title">Hadith</div>
            </div>
            <div className="hajj-hadith-body">
              {item.arabic && (
                <div className="hajj-hadith-arabic">{item.arabic}</div>
              )}
              <div className="hajj-hadith-text">« {item.text} »</div>
              {item.source && (
                <div className="hajj-hadith-source">— {item.source}</div>
              )}
              {item.context && (
                <div className="hajj-hadith-context">{item.context}</div>
              )}
              {item.reference && (
                <div className="hajj-hadith-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'hadith-multiple':
        return (
          <div key={index} className="hajj-hadith-card">
            <div className="hajj-hadith-header">
              <div className="hajj-hadith-icon">📜</div>
              <div className="hajj-hadith-title">Hadiths</div>
            </div>
            <div className="hajj-hadith-body">
              <div className="hajj-hadith-multiple">
                {item.hadiths?.map((hadith, hadithIndex) => (
                  <div key={hadithIndex} className="hajj-hadith-item">
                    <div className="hajj-hadith-number">Hadith {hadithIndex + 1}</div>
                    <div className="hajj-hadith-text">« {hadith.text} »</div>
                    {hadith.source && (
                      <div className="hajj-hadith-source">— {hadith.source}</div>
                    )}
                    {hadith.context && (
                      <div className="hajj-hadith-context">{hadith.context}</div>
                    )}
                    {hadith.reference && (
                      <div className="hajj-hadith-reference">{hadith.reference}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'athar':
        return (
          <div key={index} className="hajj-athar-card">
            <div className="hajj-athar-header">
              <div className="hajj-athar-icon">📜</div>
              <div className="hajj-athar-title">Athar</div>
            </div>
            <div className="hajj-athar-body">
              <div className="hajj-athar-text">« {item.text} »</div>
              {item.source && (
                <div className="hajj-athar-source">— {item.source}</div>
              )}
              {item.context && (
                <div className="hajj-athar-context">{item.context}</div>
              )}
              {item.reference && (
                <div className="hajj-athar-reference">{item.reference}</div>
              )}
            </div>
          </div>
        );

      case 'athar-multiple':
        return (
          <div key={index} className="hajj-athar-card">
            <div className="hajj-athar-header">
              <div className="hajj-athar-icon">📜</div>
              <div className="hajj-athar-title">Athar</div>
            </div>
            <div className="hajj-athar-body">
              <div className="hajj-athar-multiple">
                {item.hadiths?.map((athar, atharIndex) => (
                  <div key={atharIndex} className="hajj-athar-item">
                    <div className="hajj-athar-item-header">
                      <div className="hajj-athar-number">Narration {atharIndex + 1}</div>
                      {athar.context && (
                        <div className="hajj-athar-item-context">{athar.context}</div>
                      )}
                    </div>
                    <div className="hajj-athar-text">« {athar.text} »</div>
                    {athar.source && (
                      <div className="hajj-athar-source">— {athar.source}</div>
                    )}
                    {athar.reference && (
                      <div className="hajj-athar-reference">{athar.reference}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'enumeration':
        return (
          <div key={index} className={`hajj-enumeration part-${currentPart}`}>
            {(item.items as EnumerationItem[])?.map((enumItem, enumIndex) => (
              <div key={enumIndex} className="hajj-enum-item">
                <div className="hajj-enum-label">{enumItem.label}</div>
                <div className="hajj-enum-content">
                  <p className="hajj-enum-text">{enumItem.text}</p>
                  {enumItem.subtext && (
                    <p className="hajj-enum-subtext">{enumItem.subtext}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'invocation-methods':
        return (
          <div key={index} className={`hajj-invocation-methods part-${currentPart}`}>
            {item.methods?.map((method, methodIndex) => (
              <div key={methodIndex} className="hajj-method-card">
                <div className="hajj-method-header">
                  <div className="hajj-method-number">{methodIndex + 1}</div>
                  <h5 className="hajj-method-title">{method.title}</h5>
                </div>
                {method.description && (
                  <p className="hajj-method-description">{method.description}</p>
                )}
                {method.invocation && (
                  <div className="hajj-method-invocation">
                    <div className="hajj-invocation-arabic">{method.invocation.arabic}</div>
                    <div className="hajj-invocation-phonetic">{method.invocation.phonetic}</div>
                    <div className="hajj-invocation-translation">{method.invocation.translation}</div>
                  </div>
                )}
                {method.instruction && (
                  <p className="hajj-method-instruction">{method.instruction}</p>
                )}
              </div>
            ))}
            {item.conclusion && (
              <div className="hajj-methods-conclusion">{item.conclusion}</div>
            )}
          </div>
        );

      case 'part-marker':
        currentPart = item.number || 0;
        return <div key={index} className={`hajj-part-marker part-${item.number}`} />;

      case 'day-title':
        currentPart = item.number || 0;
        return (
          <div key={index} className={`hajj-day-title day-${item.number}`}>
            <div className="hajj-day-number">{item.number}</div>
            <h2 className="hajj-day-title-text">{item.text}</h2>
          </div>
        );

      case 'separator':
        return <hr key={index} className="hajj-separator" />;

      case 'conclusion-card': {
        const renderConclusionParagraph = (text: string) => {
          const parts = text.split(/(«[^»]*»)/g);
          return parts.map((part, i) =>
            part.startsWith('«')
              ? <span key={i} className="hajj-conclusion-quote">{part}</span>
              : part
          );
        };
        return (
          <div key={index} className="hajj-conclusion-card">
            <div className="hajj-conclusion-header">
              <div className="hajj-conclusion-icon">🕋</div>
              <h3 className="hajj-conclusion-title">{item.title}</h3>
            </div>
            <div className="hajj-conclusion-main">
              <p className="hajj-conclusion-message">{item.mainMessage}</p>
            </div>
            <div className="hajj-conclusion-content">
              {item.content?.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{renderConclusionParagraph(paragraph)}</p>
              ))}
            </div>
          </div>
        );
      }

      case 'summary-table':
        return (
          <div key={index} className="hajj-summary-table">
            <div className="hajj-summary-table-header">
              <span>📋</span>
              <span>{item.title}</span>
            </div>
            <table className="hajj-summary-table-grid">
              <thead>
                <tr>
                  {item.headers?.map((h, i) => <th key={i}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {item.rows?.map((row, rIndex) => (
                  <tr key={rIndex}>
                    {row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {item.footer && item.footer.length > 0 && (
              <div className="hajj-summary-table-footer">
                {item.footer.map((f, fIndex) => (
                  <div key={fIndex} className="hajj-summary-table-total">
                    <span className="hajj-summary-total-label">{f.label}</span>
                    <span className="hajj-summary-total-value">{f.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'author-note':
        return (
          <div key={index} className={`hajj-author-note part-${currentPart}`}>
            <div className="hajj-author-note-header">
              <span>⚠️</span>
              <span>Note importante — {item.title}</span>
            </div>
            <div className="hajj-author-note-body">
              {item.sections?.map((section, sIndex) => (
                <div key={sIndex} className="hajj-author-note-section">
                  <div className="hajj-author-note-label">{section.label}</div>
                  <p className="hajj-author-note-text">{section.text}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'qa-card': {
        const renderQaText = (text: string): React.ReactNode => {
          if (!text.includes('**')) return text;
          const parts = text.split(/(\*\*[^*]+\*\*)/g);
          return <>{parts.map((part, i) =>
            part.startsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
          )}</>;
        };
        return (
          <div key={index} className="hajj-qa-card">
            <div className="hajj-qa-header">
              <span className="hajj-qa-icon">🎓</span>
              <div>
                <div className="hajj-qa-label">{item.label || 'Question posée au'}</div>
                <div className="hajj-qa-sheikh">{item.sheikh}</div>
              </div>
            </div>
            {item.caption && (
              <div className="hajj-qa-caption">
                <span className="hajj-qa-caption-icon">⚠️</span>
                <span>{item.caption}</span>
              </div>
            )}
            <div className="hajj-qa-body">
              {item.qa?.map((qa, qaIndex) => (
                <div key={qaIndex} className="hajj-qa-item">
                  {qa.question && (
                    <div className="hajj-qa-question">
                      <p>{qa.question}</p>
                    </div>
                  )}
                  <div className="hajj-qa-answer">
                    {qa.answer.split('\n\n').map((para, pIndex) => (
                      <p key={pIndex}>{renderQaText(para)}</p>
                    ))}
                    {qa.points && qa.points.length > 0 && (
                      <ol className="hajj-qa-points">
                        {qa.points.map((point, pIndex) => (
                          <li key={pIndex}>{renderQaText(point)}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {item.link && (
              <div className="hajj-qa-link-wrap">
                <a href={item.link.href} className="hajj-qa-link-btn">
                  {item.link.label}
                </a>
              </div>
            )}
            {item.source && (
              <a href={item.source} className="hajj-qa-source" target="_blank" rel="noopener noreferrer">
                🎥 Voir la source (vidéo)
              </a>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="hajj-guide-page">
      <Navigation />

      <SEO
        title="Guide complet du Ḥajj"
        description="Guide détaillé pour accomplir le Ḥajj selon la Sunnah du Prophète ﷺ. Basé sur l'enseignement de Cheykh Souleymâne ar-Rouheylī avec invocations en arabe, phonétique et traduction."
        url="https://mahi-bayan.netlify.app/guide-hajj"
      />

      <div className="hajj-guide-content">
        <Header
          mainTitle={["GUIDE DU ḤAJJ COMPLET"]}
          subtitle={`Élaboration du guide en se basant sur la conférence de Cheykh Souleymâne ar-Rouheylī
intitulé "خُذُوا عَنِّي مَنَاسِكَكُمْ" à la Mosquée du Prophète ﷺ le Samedi 6 Dhū al-Ḥijjah 1436 H`}
        />

        <main className="hajj-guide-main">
          <div className="hajj-guide-wrapper">
            {data.content.map((item, index) => renderContentItem(item, index))}
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default HajjGuidePage;
